import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const ciscoCaseStudy: ContentObject = {
    id: 'cisco-case-study',
    kind: 'system',
    type: 'case-study',
    name: 'GenAI Aids Routine Coding Tasks at Cisco',
    description:
        'How GenAI streamlines routine coding and debugging while highlighting the need for human oversight.',
    url: null, // System content, no external URL
    scores: createScores({
        detailedMetrics: 8.0,
        relevanceToUseCase: 10.0,
        reliabilityOfSource: 10.0,
        strategicAndTacticalInformation: 9.0,
        technicalImplementationDetails: 10.0,
        noVendorBias: 10.0,
        relevanceToClient: 9.0,
    }),
    publishedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
    markdown: `# Case Study: Enhancing Developer Productivity with GenAI
 
 This case study explores how a large technology company is leveraging generative AI (GenAI) tools to boost software development productivity. The insights come from a private interview conducted by Varos with a Senior Software Engineer at Cisco, who works primarily on backend systems, infrastructure management, and debugging in a modern development environment.
 
 ---
 
 ## 1. Source of Information
 
 **Who the Source Is:** 
 The insights are drawn from a private interview conducted by Varos with a Senior Software Engineer at Cisco. The interviewee is responsible for backend development, infrastructure management, and code maintenance at a large technology organization. Their role includes designing architectures and resolving networking and system bugs, making them an ideal subject for discussing the impact of GenAI on software development practices.
 
 **Relevant Quote:** 
 > ""I'm operating in a large networking company and like my general role is mostly backend, setting up infrastructure and solving various networking problems."" 
 > – *Senior Software Engineer at Cisco*
 
 ---
 
 ## 2. Successes
 
 **Automated Code Assistance and Maintenance**
 
 **GitHub Copilot for Legacy Code Updates – Modular Code Generation:** 
 The engineer leverages Copilot to generate small, modular functions that aid in understanding and updating legacy code. This process involves opening a file, typing a question in a dedicated pane, and receiving guidance on the file's contents. Although the fix may not be perfect, it provides valuable direction. 
 > "You can open a file, type your question in the pane, and it will show you what's in that code file. It may not write the perfect fix, but it helps guide you to update the old code." 
 > – *Senior Software Engineer at Cisco*
 
 **Enhanced Debugging with Sample Outputs:** 
 Instead of manually inserting multiple log statements, Copilot supplies expected inputs and outputs for functions. This feature accelerates the debugging process by offering quick insights, thereby reducing reliance on traditional, time-consuming debugging methods.
 
 **Assistance with Unit Testing and Documentation:** 
 Copilot also assists in generating unit tests for well‑structured functions and adds necessary documentation. This automated support enhances code maintainability and accelerates overall improvements in code quality.
 
 **Strategic Impact on Workflow – Time Reallocation and Increased Productivity:** 
 By automating routine maintenance tasks, the engineer now dedicates more time to system architecture design and strategic planning. Faster shipping cycles and more stable code have led to a reduction in time spent on bug fixes, allowing more focus on innovation. In optimal scenarios, AI tools can reduce the time spent on specific debugging or scripting tasks by up to one-tenth, significantly contributing to overall productivity gains.
 
 ---
 
 ## 3. Challenges
 
 **Limitations of AI Models:** 
 The interview highlighted that while GenAI tools are beneficial, they come with inherent challenges. One major limitation is that these models are trained only up to a certain cutoff date. Consequently, they may not recognize or work seamlessly with new libraries or packages that have emerged since the training data was collected.
 
 **Context and Complexity:** 
 Another challenge involves the AI's struggle to comprehend complex or poorly structured code. When code spans multiple function calls or exhibits inconsistent design patterns, the AI might provide a solution that requires additional human refinement. As a result, human oversight remains essential to verify and adjust AI-generated outputs.
 
 **Relevant Quotes:** 
 > ""It's trained to a certain cutoff date, right? That's the problem... if it's a bit complicated code, then it's a problem sometimes."" 
 > – *Senior Software Engineer at Cisco*
 
 > ""I think it's like, you need to have a human in the loop. You don't have to trust everything the AI gives – we have to have our eye on that."" 
 > – *Senior Software Engineer at Cisco*
 
 ---
 
 ## 4. Technical Details
 
 **Tools and Integrations:** 
 The engineering team integrates several GenAI solutions into their development environment. GitHub Copilot is embedded directly within the integrated development environment (IDE) (for example, VS Code), providing suggestions for writing code, generating unit tests, and adding documentation. The engineer also uses an in-house version of ChatGPT that maintains conversation context and leverages company-specific data to generate tailored code snippets and debug outputs.
 
 **Programming Languages and Debugging:** 
 The primary programming languages in use are Go and Python. The AI tools assist in generating unit tests and in debugging by predicting expected function inputs, outputs, and even sample behavior. This capability helps streamline the debugging process, reducing the need to manually insert log statements and run full-scale tests.
 
 **Additional Technical Use Cases:** 
 Beyond code generation, the tools are used to automatically generate documentation and to create architecture diagrams from plain English instructions. Although some tools for diagram generation are available, the interviewee noted that they primarily focus on backend code and infrastructure, leaving UI and design tools for other teams.
 
 **Relevant Quotes:** 
 > ""GitHub Copilot is embedded in our IDE... you just click the file and it shows what's in that code file and then, you ask it, and it sort of gives you a fix."" 
 > – *Senior Software Engineer at Cisco*
 
 > ""I would say unit tests definitely help with Copilot – if you have written the function very cleanly, it works well for mocking and testing."" 
 > – *Senior Software Engineer at Cisco*
 
 ---
 
 ## 5. Results
 
 **Efficiency and Productivity Improvements:** 
 The GenAI tools have led to tangible productivity improvements. The engineer estimates that, in optimal scenarios, these tools can save about one-tenth of the time normally spent on debugging and routine maintenance tasks. This efficiency gain has allowed developers to shift their focus from repetitive coding to more innovative tasks like architecture design and feature development.
 
 **Reduction in Maintenance Work:** 
 There has been a noticeable shift in how time is allocated during the workweek. Previously, a significant portion of the time was consumed by writing and maintaining code. With AI support, less time is needed for debugging and maintenance, which in turn has reduced the frequency of code issues and led to more stable releases.
 
 **Quantitative Impact on Linux Scripting:** 
 A striking example of efficiency gain is in the development of Linux scripts. The engineer noted that tasks which once took days to complete are now finished in about five minutes—a dramatic time reduction that illustrates the power of automated code assistance. This represents an approximate reduction of up to 90–99% in the time required for these specific tasks, allowing for faster iteration and deployment.
 
 **Impact on Innovation:** 
 With the time saved, the engineer now dedicates more hours to designing robust system architectures and exploring innovative solutions. This change in focus is believed to enhance overall product quality and accelerate the delivery of new features.
 
 **Relevant Quotes:** 
 > ""In a good scenario, it would say one-tenth of your time is saved – and with that extra time, you can ship more features or focus on higher-level design."" 
 > – *Senior Software Engineer at Cisco*
 
 > ""Now, instead of spending 30 hours coding, I spend 20–25 hours coding and more time on designing architecture."" 
 > – *Senior Software Engineer at Cisco*
 
 > ""Yeah, like those Linux scripts – we used to take days and now it takes like five minutes. Wow."" 
 > – *Senior Software Engineer at Cisco*
 
 ---
 
 ## 6. Vendors Used
 
 **Primary GenAI Vendors:** 
 The engineering team primarily utilizes Microsoft's suite of AI tools. GitHub Copilot is a central tool for code generation and debugging, while an in-house version of ChatGPT (leveraging Azure ChatGPT) is used to maintain contextual conversations and provide coding assistance. Additionally, the engineer mentioned using other tools like perplexity on a personal level, although the focus remains on the Microsoft offerings.
 
 **Market Perspective on Vendor Landscape:** 
 The discussion touched upon the broader vendor ecosystem, with the engineer noting that while many vendors are offering specialized solutions (for tasks such as diagram generation or prioritizing bug fixes), the current reliance remains on a few core providers. There is anticipation that in the near future, more vendor-specific solutions may emerge, but for now, the basic tools are considered sufficient for daily tasks.
 
 **Relevant Quotes:** 
 > ""The only AI tools that we have access to are kind of the Microsoft ones – GitHub Copilot and ChatGPT... we have the Azure ChatGPT."" 
 > – *Senior Software Engineer at Cisco*
 
 > ""I use perplexity personally, but I don't see much difference in the layers or models that are available."" 
 > – *Senior Software Engineer at Cisco*
 
 ---
 
 This case study demonstrates that while GenAI tools have become a significant asset in streamlining routine coding tasks and debugging processes—including dramatically reducing the time required for Linux scripting—human oversight remains crucial to address the limitations of current models. The balance between efficiency gains and the need for expert judgment continues to shape the evolving landscape of software development productivity.
 `,
    references: [
        {
            id: '1',
            name: 'Private Varos Interview with Cisco Engineer',
            author: 'Varos Inc.',
            year: '2024',
            publisher: 'Varos Inc.',
            url: null, // Private interview
        },
    ],
};
